/**
 * AmbientContextChips — the ambient-context chip row (Jul 13, 2026).
 *
 * The Cursor / Dia pattern: make the entity the user currently has OPEN
 * in the workspace VISIBLE and REVOCABLE right above the composer input,
 * so "the chat already knows about X" is legible instead of the agent
 * silently re-searching. Reuses the existing ``activeContextStore`` — it
 * does NOT re-capture context; it renders whatever the inline viewers
 * already registered via ``useActiveContextScope``.
 *
 * Behaviour
 * ---------
 *   - Renders a small hairline pill for the open entity (kind icon +
 *     title + ✕). The store holds a single ``active`` today, so this is
 *     one chip; the row is a ``flex-wrap`` so multiple chips would tuck
 *     into a tight line if the store grows to a list.
 *   - The ✕ calls ``dismiss()`` → the open entity is dropped from the
 *     NEXT send(s) (see ``useSendQuery`` drop-logic, which reads the same
 *     ``isDismissed`` flag). It re-appears when the user opens a new item.
 *   - Hidden when an explicit scope already suppresses the entity
 *     (``isOverridden``) — that case is covered by
 *     ``ActiveContextOverrideBanner``, so the two never contradict. Also
 *     hidden once dismissed. So the chip is shown ONLY when the open
 *     entity will actually ground the answer — an honest ambient signal.
 *
 * Pencil aesthetic: hairline contour pill, warm ``bg-white/45``, ONE
 * accent (the tiny kind icon in ``text-ando-orange``); title idle
 * ``text-ink-2`` → hover ``text-ink-1``. No filled orange wash.
 */
import type * as React from "react";
import {
  Calendar,
  FileText,
  Mail,
  PenLine,
  Quote,
  Share2,
  Workflow,
  X,
  type LucideIcon,
} from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";

import { cn } from "@ando/ui";

import { useActiveContextOverride } from "../hooks/useActiveContextOverride";
import type { ActiveContextKind } from "../types";

/** Kind-specific Lucide glyph — the ONE accent-coloured mark on the chip. */
const KIND_ICON: Record<ActiveContextKind, LucideIcon> = {
  calendar_event: Calendar,
  document: FileText,
  inbox_thread: Mail,
  source_citation: Quote,
  canvas: PenLine,
  connect_space: Share2,
  flow: Workflow,
};

export interface AmbientContextChipsProps {
  readonly className?: string;
}

export const AmbientContextChips: React.FC<AmbientContextChipsProps> = ({
  className,
}) => {
  const { active, isOverridden, isDismissed, dismiss } =
    useActiveContextOverride();
  const { i18n } = useLingui();

  // Show only when the open entity will actually ground the answer: an
  // explicit-scope override or an explicit dismissal both hide it.
  if (!active || isOverridden || isDismissed) return null;

  const Icon = KIND_ICON[active.kind] ?? FileText;
  const title = active.title?.trim() || i18n._(msg`Elemento aperto`);
  const dismissLabel = i18n._(
    msg`Non usare "${title}" in questa risposta`,
  );

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-1.5",
        "motion-safe:fade-in motion-safe:slide-in-from-bottom-1 motion-safe:animate-in motion-safe:duration-150",
        className,
      )}
    >
      <span
        className={cn(
          "group inline-flex max-w-full items-center gap-1.5 rounded-full",
          "border border-hairline bg-white/45 px-2 py-0.5 text-[12px]",
        )}
        // Hover tooltip: the pre-computed one-line summary the viewer
        // registered (sender+subject, datetime+location, …). Falls back
        // to the title so there's always something to read.
        title={active.summary?.trim() || title}
      >
        <Icon
          size={13}
          strokeWidth={2}
          className="shrink-0 text-ando-orange"
          aria-hidden
        />
        <span className="min-w-0 max-w-[28ch] truncate text-ink-2 transition-colors group-hover:text-ink-1">
          {title}
        </span>
        <button
          type="button"
          onClick={dismiss}
          aria-label={dismissLabel}
          className={cn(
            "-mr-0.5 inline-flex shrink-0 items-center justify-center rounded-full p-0.5",
            "text-ink-4 transition-colors hover:text-ink-2",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
          )}
        >
          <X size={12} strokeWidth={2.25} aria-hidden />
        </button>
      </span>
    </div>
  );
};
AmbientContextChips.displayName = "AmbientContextChips";
